import React, { Component } from "react";
import { observable } from "mobx";
import { observer, inject } from "mobx-react";
import { Descriptions, Tag, Tooltip } from "antd";
import { AppStore } from "~/stores/AppStore";
import { awesomeIcon } from "../../../utils/helpers";
import { translateKey as t } from "../../../locales/translation";

import dayjs from "dayjs";
import _ from "lodash";
import "./Home.scss";
import PageSettings from "./PageSettings";

import advancedFormat from "dayjs/plugin/advancedFormat";
import { CountdownWrapper } from "../../../components/status_bar/CountdownWrapper";
dayjs.extend(advancedFormat);

@inject("store")
@observer
export class Home extends Component<{
  store?: AppStore;
  path?: string;
}> {
  constructor(props: any) {
    super(props);
    this.props.store.selectedClient = "generic";
  }

  private initialSetupData: any = {
    depots: [],
    drivers: [],
    vehicles: [],
    users: [],
  };

  @observable private unfinishedSetups: any = [];

  private getCatIcon = (icon: any) => {
    return awesomeIcon(icon, "1x");
  };

  public componentDidMount() {
    if (process.env.PLATFORM === "online") {
      this.loadDependencies();
    }

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("tokenType") || urlParams.get("session_id")) {
      window.history.replaceState({}, "", "/home");
    }
  }

  private async loadDependencies() {
    let promises = [];

    promises.push(this.loadDepots());
    promises.push(this.loadUsers());
    promises.push(this.loadDrivers());
    promises.push(this.loadVehicles());

    Promise.all(promises).then(() => {
      this.checkSetupFinished();
    });
  }

  private async loadDepots() {
    await this.props.store.getData("depots").then((response) => {
      this.initialSetupData.depots = response;
    });
  }

  private async loadUsers() {
    await this.props.store.getData("users").then((response) => {
      this.initialSetupData.users = response;
    });
  }

  private async loadDrivers() {
    await this.props.store.getData("drivers").then((response) => {
      this.initialSetupData.drivers = response.data;
    });
  }

  private async loadVehicles() {
    await this.props.store.getData("vehicles").then((response) => {
      this.initialSetupData.vehicles = response.data;
    });
  }

  private checkSetupFinished() {
    Object.keys(this.initialSetupData).map((data) => {
      if (this.initialSetupData[data].length === 0) {
        this.unfinishedSetups.push(data.charAt(0).toUpperCase() + data.slice(1)); //push capitalised name to array
      }
      return null;
    });
  }

  private renderRoles = (roles) => {
    const elements: any = [];

    roles.forEach((r) => {
      elements.push(
        <Tag key={r} color="geekblue" className="mr-4">
          {r}
        </Tag>
      );
    });

    return elements;
  };

  private renderLoginHistory = (historyData) => {
    const elements = [];

    historyData.forEach((sl, i) => {
      elements.push(
        <div key={i} className="flex pb-2">
          <div className="flex-1">{dayjs(sl.createdAt).format("ddd Do MMM, YYYY")}</div>
          <div>{dayjs(sl.createdAt).format("HH:mm")}</div>
        </div>
      );
    });

    return (
      <div className="loginHistory">
        {elements.length > 0 && (
          <div className="flex font-bold pb-2">
            <div className="flex-1">Date</div>
            <div>Time</div>
          </div>
        )}

        {elements.length > 0 ? <div>{elements}</div> : <div className="text-center">{"No information available"}</div>}
      </div>
    );
  };

  private renderPageStats = (type, pages) => {
    const elements = [];

    if (!pages) {
      return <div className="text-center">{"No information available"}</div>;
    }

    pages[type].forEach((p, i) => {
      elements.push(
        <Tooltip
          key={p.path + i}
          mouseLeaveDelay={0}
          title={
            <>
              <b>{t(p.translationKey, p.name)}</b>
              {" - "}
              <span>{t(p.routeGroupTranslationKey, p.routeGroup)}</span>
            </>
          }
        >
          <div className="mostVisitedPages" onClick={() => (window.location = p.path)}>
            {this.getCatIcon(p.icon)}
            <div className="truncate flex-1">
              <b>{t(p.translationKey, p.name)}</b>
              {" - "}
              <span>{t(p.routeGroupTranslationKey, p.routeGroup)}</span>
            </div>
          </div>
        </Tooltip>
      );
    });

    return elements.length > 0 ? (
      <div>{elements}</div>
    ) : (
      <div className="text-center">{"No information available"}</div>
    );
  };

  private renderProvider = (type) => {
    switch (type) {
      case "google":
        return (
          <Tag color="magenta">
            <div className="flex items-center">
              {/* <Icon type="google" style={{ marginRight: 5 }} /> */}
              {awesomeIcon("google", "xs")}
              <span>Google</span>
            </div>
          </Tag>
        );

      case "azuread":
        return (
          <Tag color="cyan">
            <div className="flex items-center">
              {/* <Icon type="windows" style={{ marginRight: 5 }} /> */}
              {awesomeIcon("windows", "1x", { marginRight: 5 }, "brand")}
              <span>Microsoft</span>
            </div>
          </Tag>
        );

      case "local":
        return (
          <Tag color="purple">
            <div className="flex items-center">
              {awesomeIcon("home", "xs")}
              {/* <Icon type="home" style={{ marginRight: 5 }} /> */}
              <span>{t("lblLocal", "Local")}</span>
            </div>
          </Tag>
        );

      case "mlauth":
        return (
          <Tag color="purple">
            <div className="flex items-center">
              {awesomeIcon("cloud", "xs")}
              {/* <Icon type="cloud" style={{ marginRight: 5 }} /> */}
              <span>{t("lblMlauth", "MLAuth")}</span>
            </div>
          </Tag>
        );

      default:
        return t("lblUnspecified", "Unspecified");
    }
  };

  private renderExternalLinks = (externalLinks) => {
    return externalLinks.map((link, i) => {
      return (
        <Tooltip title={link.tooltip} key={i}>
          <Tag color="volcano" onClick={() => window.open(link.url, "_blank")} className="cursor-pointer">
            <div className="flex items-center">
              <img style={{ marginRight: 5, width: 15 }} src={link.icon} alt="" />
              <span style={{ marginRight: 5 }}>{link.name}</span>
              {awesomeIcon("external-link-alt", "xs")}
            </div>
          </Tag>
        </Tooltip>
      );
    });
  };

  private greeting = () => {
    const hour = dayjs().hour();

    if (hour > 17) {
      return t("lblGoodEvening", "Good Evening");
    }

    if (hour > 11) {
      return t("lblGoodAfternoon", "Good Afternoon");
    }

    return t("lblGoodMorning", "Good Morning");
  };

  public render() {
    const { user } = this.props.store;

    const pages = this.props.store.getPageStats(4);
    const externalLinks = this.props.store.getClientPreference("externalLinks", [], true);
    const copyRightNotice = this.props.store.getClientPreference("copyRightNotice", null, true);

    return (
      <div className="welcome--container md:mt-7">
        <div className="welcome--dashboard">
          <div className="mainContainer">
            <div style={{ marginRight: 20 }}>{/* <SpotlightSearch /> */}</div>
          </div>

          <div className="mainContainer">
            <div className="dashCard md:m-0 m-5">
              <div className="header mb-10">
                <div className="title flex-1">
                  <b>{this.greeting()},</b>
                  <div>{user.firstName + " " + user.lastName}</div>
                </div>

                <div className="logo" />
              </div>

              <Descriptions bordered={true} column={1} size="small">
                <Descriptions.Item label={<b>{t("lblRoles", "Roles")}</b>}>
                  {this.renderRoles(user.roles)}
                </Descriptions.Item>
                {user.provider === "local" && (
                  <Descriptions.Item label={<b>{t("lblPasswordExpires", "Password Expires")}</b>}>
                    {/* {dayjs(user.passwordExpiryDate).from(dayjs())} */}
                    <span> {t("lblOnLabelDate", "On")} </span>
                    {dayjs(user.passwordExpiryDate).format("ddd Do MMM, YYYY")}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={<b>{t("lblLoginSession", "Login Session")}</b>}>
                  <CountdownWrapper
                    expires={dayjs(user.expires, "x")}
                    format="Dd HH:mm"
                    style={{
                      fontSize: "12px",
                      color: "#666",
                    }}
                    onExpiry={() => null}
                  />
                </Descriptions.Item>
                <Descriptions.Item label={<b>{t("lblLoginProvider", "Login Provider")}</b>}>
                  {this.renderProvider(user.provider)}
                </Descriptions.Item>
                <Descriptions.Item label={<b>{t("lblUserCreated", "User Created")}</b>}>
                  {dayjs(user.createdAt).format("ddd Do MMM, YYYY")}
                </Descriptions.Item>
                <Descriptions.Item label={<b>{t("lblEmailAddress", "Email Address")}</b>}>
                  {user.email}
                </Descriptions.Item>
                {externalLinks.length > 0 && (
                  <Descriptions.Item label={<b>External Links</b>}>
                    {this.renderExternalLinks(externalLinks)}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          </div>

          <div className="sideContainer inline-flex flex-wrap justify-center gap-x-6 md:mt-8" id="pageSettings">
            <div className="dashCard w-full md:w-64">
              <div className="header">
                <div className="title">
                  <b>{t("lblSuccessfulLogins", "Successful Logins")}</b>
                </div>
                <div>{this.renderLoginHistory(user.successLogins)}</div>
              </div>
            </div>

            <div className="dashCard w-full md:w-64">
              <div className="header">
                <div className="title">
                  <b>{t("lblFailedLogins", "Failed Logins")}</b>
                </div>
                <div>{this.renderLoginHistory(user.failedLogins)}</div>
              </div>
            </div>

            <div className="dashCard w-full md:w-64">
              <div className="header">
                {_.get(pages, "most", []).length > 0 && (
                  <Tooltip title={t("lblViewAllMostVisitedPages", "View All Most Visited Pages")}>
                    <div className="settings" onClick={() => this.props.store.pageSettings.openModal("most")}>
                      {awesomeIcon("cog", "1x")}
                    </div>
                  </Tooltip>
                )}
                <div className="title">
                  <b>{t("lblMostVisitedPages", "Most Visited Pages")}</b>
                </div>
                <div>{this.renderPageStats("most", pages)}</div>
              </div>
            </div>

            <div className="dashCard w-full md:w-64">
              <div className="header">
                {_.get(pages, "recent", []).length > 0 && (
                  <Tooltip title={t("lblViewAllMostRecentPages", "View All Most Recent Pages")}>
                    <div className="settings" onClick={() => this.props.store.pageSettings.openModal("recent")}>
                      {awesomeIcon("cog", "1x")}
                    </div>
                  </Tooltip>
                )}
                <div className="title">
                  <b>{t("lblMostRecentPages", "Most Recent Pages")}</b>
                </div>
                <div>{this.renderPageStats("recent", pages)}</div>
              </div>
            </div>
          </div>
        </div>

        <PageSettings ref={(ref) => (this.props.store.pageSettings = ref)} />

        {copyRightNotice && <div className="copyRightNotice">{copyRightNotice}</div>}
      </div>
    );
  }
}
